import makeRequest from '@jg/http-client';
import { Auth } from '@jg/auth';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import { AccessToken } from '@jg/auth/es/types/AccessToken';
import { httpActions, appActions } from '..';
import {
  REQUEST,
  ROUTES,
  ERROR_MESSAGES,
  DEFAULT_FRP_COVER_PHOTO,
} from '../../consts';
import config from '../../config';
import { IAppState, IMembershipDefaultsDto } from '../../types/state.d';
import linkFundraisingPage from './linkFundraisingPage';
import logInfo from './logInfo';
import {
  generateAuthHeaders,
  redirectByResponseStatus,
  getMembershipDefaultsDto,
} from '../../utils';
import {
  getEventId,
  getCurrencyCode,
  getTeamGuid,
  getCampaignGuid,
  getCharityId,
} from '../../selectors/apiSelectors';
import { getSelectedEvent } from '../../selectors/appSelectors';
import pushToPage from './pushToPage';
import updateGDPRConsents from './updateGDPRConsents';
import { IGiftAidFormProps } from '../../containers/GiftAid/GiftAid.d';

export type RegisterFundraisingPageDto = {
  charityId: number;
  eventId: number;
  pageShortName: string;
  pageTitle: string;
  targetAmount: number | string;
  charityOptIn: boolean;
  pageStory: string | null;
  currency: string;
  charityFunded: boolean;
  isGiftAidable?: boolean;
  campaignGuid: string | null;
  coverPhoto?: string | null;
  backgroundColour?: string | null;
  pageBackground?: string | null;
  buttonColour?: string | null;
  titleColour?: string | null;
  buttonsThermometerFill?: string | null;
  images?: [{ url: string | null }];
};

type CreateFundingPage = {
  pageShortName: string;
  targetAmount?: string | number;
  gdprOptIn?: boolean;
  giftAidData?: Pick<
    IGiftAidFormProps,
    'askingForDonation' | 'contributingToCost'
  >;
};

const createFundraisingPage = ({
  pageShortName,
  targetAmount = '0',
  gdprOptIn,
  giftAidData,
}: CreateFundingPage) => async (
  dispatch: ThunkDispatch<IAppState, Auth, Action>,
  getState: () => IAppState,
  auth: Auth,
) => {
  const resourceName = REQUEST.CREATE_FUNDRAISING_PAGE;
  try {
    await auth.refreshAccessTokenIfExpired();
    dispatch(httpActions.httpRequestMade(resourceName));
    const state = getState();
    const accessToken = auth.getAccessToken() as AccessToken;
    const pageTitle = `${accessToken.givenName}’s page`;
    const charityId = getCharityId(state);
    const teamEventId = getEventId(state);
    const selectedEvent = getSelectedEvent(state);
    const teamGuid = getTeamGuid(state);
    const currency = getCurrencyCode(state);
    const campaignGuid = getCampaignGuid(state);

    /* Get membership defaults  */
    const membershipDefaultsResponse = await makeRequest(
      `${config.pagesAPI}/v1/teams/${teamGuid}/membership/defaults`,
      {
        method: 'GET',
        extraHeaders: {
          ...generateAuthHeaders(auth),
        },
      },
    );
    if (membershipDefaultsResponse && !membershipDefaultsResponse.ok) {
      redirectByResponseStatus(membershipDefaultsResponse.status);
      throw new Error(ERROR_MESSAGES.GET_MEMBERSHIP_DEFAULTS);
    }
    const membershipDefaults: IMembershipDefaultsDto = await membershipDefaultsResponse.json();
    const body: RegisterFundraisingPageDto = {
      charityId,
      eventId: (selectedEvent && selectedEvent.id) || teamEventId,
      pageShortName,
      pageTitle,
      targetAmount: targetAmount === '' ? '0' : targetAmount,
      charityOptIn: false,
      charityFunded: false,
      pageStory: '',
      currency,
      campaignGuid,
      images: [
        {
          url: `${config.imageAPIBaseURL}/${
            membershipDefaults.coverPhoto
              ? membershipDefaults.coverPhoto
              : DEFAULT_FRP_COVER_PHOTO
          }`,
        },
      ],
      ...getMembershipDefaultsDto(membershipDefaults),
    };
    /* Set giftAid defaults  */
    if (giftAidData) {
      body.charityFunded = giftAidData.contributingToCost;
      body.isGiftAidable = !giftAidData.askingForDonation;
    }

    const response = await makeRequest(config.frpAPI, {
      method: 'PUT',
      body: JSON.stringify(body),
      credentials: 'same-origin',
      timeoutInMs: config.frpAPITimeoutInMs,
      extraHeaders: {
        ...generateAuthHeaders(auth),
      },
    });
    if (response && !response.ok) {
      redirectByResponseStatus(response.status);
      const logData = {
        tags: ['api_failure'],
        message: ERROR_MESSAGES.CREATE_FUNDRAISING_PAGE,
        method: 'PUT',
        body,
        res: response.bodyUsed ? null : await response.json(),
      };
      dispatch(logInfo(logData));
      throw new Error(ERROR_MESSAGES.CREATE_FUNDRAISING_PAGE);
    }

    const guidResponse = await makeRequest(
      `${config.frpAPIv2}/byslug/${pageShortName}`,
      {
        credentials: 'same-origin',
      },
    );
    if (guidResponse && !guidResponse.ok) {
      redirectByResponseStatus(response.status);
      throw new Error(ERROR_MESSAGES.GET_FRP_GUID);
    }

    const { pageId } = await response.json();
    const { pageGuid } = await guidResponse.json();

    await dispatch(linkFundraisingPage(pageGuid));
    if (gdprOptIn !== undefined) {
      await dispatch(updateGDPRConsents(pageId, gdprOptIn));
    }
    dispatch(appActions.saveFrpId(pageId));
    dispatch(httpActions.httpRequestSuccess(resourceName));
    dispatch(
      pushToPage(`${ROUTES.JOINED_WITH_NEW_FRP}?shortName=${pageShortName}`),
    );
  } catch (error) {
    dispatch(httpActions.httpRequestError(resourceName, error));
    redirectByResponseStatus(error.status);
  }
};

export default createFundraisingPage;
